import type { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import duration from 'dayjs/plugin/duration';
import { allowMultipleToast, showConfirmDialog, showToast } from 'vant';
import iconDiamond from '@/assets/img/icon-diamond.png';
import { setShareConfig } from './share';
import { useUserStore } from '@/stores';
import router from '@/router';
import { reqExchageToken } from '@/api/brushteeth';
import { getKeXieAppToken, isKeXieApp, loginByKeXieApp } from './ccwork';
dayjs.extend(duration);

export function setDefaultShareConfig() {
  setShareConfig({
    title: '科普中国Lite',
    desc: '高效、便捷、智能，让科学触手可及',
    img: 'https://pqnoss.kepuchina.cn/2024/01/12/16/195746/de4d80.png',
  });
}

/**
 * 获取应用下载链接
return 'javascript:void(0);'
 * @returns {string} 在科协App内返回空链接，否则返回应用宝下载链接
 */
export function appLink() {
  if (isKeXieApp()) {
    return 'javascript:void(0);';
  }
  return 'https://a.app.qq.com/o/simple.jsp?pkgname=com.sevenplus.chinascience';
}
/**
 * 打开下载链接
 *
 */
export function downloadLink(url = appLink()) {
  if (!url || url === 'javascript:void(0);') {
    return;
  }
  if (isApp() || isKeXieApp()) {
    window.location.href = url;
    return;
  }
  window.open(url);
}

/**
 * 显示加分toast
 *
 * @export
 * @param {string} str 提示加分文本
 * @return {*}
 */
export function showScoreToast(str: string) {
  if (!str?.trim()) {
    return;
  }
  // 允许多个toast，防止被覆盖
  allowMultipleToast();
  const message = `<div class='show-score-toast'><div class='show-score-toast-msg'>${str}</div><img class='show-score-toast-img' src='${iconDiamond}'/></div>`;
  showToast({
    type: 'html',
    message: message,
    duration: 2000,
  });
}

/**
 * 显示下载对话框。
 *
 * @param {1 | 2 | 3} type - 下载对话框的类型，1：内容分享，2：内容点赞，3：AI小科对话
 * @return {void}
 */
export function showDownloadDialog(type: 1 | 2 | 3) {
  const msgObj = {
    1: '小程序内今日内容分享次数已达上限。若需获取更多积分，请下载使用科普中国客户端',
    2: '下载科普中国客户端，完成内容点赞可获得更多积分奖励',
    3: '下载科普中国客户端完成登录后，可继续与AI小科进行对话',
  };
  showConfirmDialog({
    title: '温馨提示',
    message: msgObj[type] || '',
    // theme: 'round-button',
    messageAlign: 'left',
    confirmButtonText: '立即下载',
    cancelButtonText: '我知道了',
  }).then(() => {
    downloadLink();
  });
}

/**
 * 日期时间格式化
 *
 * @export
 * @param {Date | number | string} [datetime=new Date()] 日期时间
 * @param {string} [format="YYYY-MM-DD HH:mm:ss"] 格式化模板
 * @return {string} 格式化后的日期时间
 */
export function datetimeFormat(
  datetime: number | string | Date = new Date(),
  format = 'YYYY-MM-DD HH:mm:ss'
) {
  if (typeof datetime === 'number' && String(datetime).length === 10) {
    datetime *= 1000;
  }
  return dayjs(datetime).format(format);
}

/**
 * 毫秒数格式化为日期时间
 *
 * @export
 * @param {number} milliseconds 毫秒数
 * @param {string} [format="mm′ss″"] 格式化模板
 * @returns {string}
 */
export function durationFormat(milliseconds: number, format = 'mm′ss″') {
  if (isNaN(milliseconds)) {
    return milliseconds;
  }
  return dayjs.duration(Number(milliseconds)).format(format);
}

// 时间戳转为10位的
export function to10(timestamp: number) {
  if (String(timestamp) === '') {
    return '';
  }
  if (String(timestamp).length !== 13) {
    throw '请传入13位时间戳';
  }
  return String(timestamp).substring(0, 10);
}

/**
 * 百分比
 *
 * @export
 * @param {number} val 数值
 * @param {number} [precision=2] 精度（小数点后几位）
 * @returns {string}
 *
 */
export function percentFormat(val: number, n = 2) {
  val = val * 100;
  n = n ? parseInt(String(n), 10) : 0;
  if (n <= 0) {
    return Math.round(val);
  }
  val = Math.round(val * Math.pow(10, n)) / Math.pow(10, n); //四舍五入
  return Number(val).toFixed(n); //补足位数
}

/**
 * 数字格式化，三位逗号分割
 *
 * @export
 * @param {number} num 数值
 * @param {number} [precision=2] 精度（小数点后几位）
 * @returns {string}
 *
 */
export function numberFormat(num: number, precision = 2) {
  num = Math.round(num * Math.pow(10, precision)) / Math.pow(10, precision);
  const str = num.toString();
  const reg =
    str.indexOf('.') > -1 ? /(\d)(?=(\d{3})+\.)/g : /(\d)(?=(?:\d{3})+$)/g;
  return str.replace(reg, '$1,');
}

/**
 * axios下载文件
 * @param {AxiosResponse} response axios的response
 * @param {string} [filename] 文件名称
 * @param {string} [fileType] 文件类型
 */
export function downloadFile(
  response: AxiosResponse,
  filename: string,
  fileType: string
) {
  let blob = null;
  if (response.config.responseType === 'arraybuffer') {
    blob = new Blob([response.data], {
      type: fileType || response?.headers?.['content-type'],
    });
  } else if (response.config.responseType === 'blob') {
    blob = response.data;
  } else {
    throw Error('下载文件的responseType必须为arraybuffer或blob');
  }
  const href = window.URL.createObjectURL(blob);
  const downloadElement = document.createElement('a');
  downloadElement.href = href;
  downloadElement.download =
    filename ||
    decodeURIComponent(
      response.headers['content-disposition']?.split('filename=')?.[1] ?? ''
    );
  document.body.appendChild(downloadElement);
  downloadElement.click();
  document.body.removeChild(downloadElement);
  window.URL.revokeObjectURL(href);
}

/**
 * url下载文件
 * @param {string} [fileName] 文件名称
 * @param {string} [url] 文件的路径
 */
export function downloadUrl(url: string, fileName: string) {
  if ('ActiveXObject' in window) {
    window.open(url, '_blank');
  } else {
    const request = new XMLHttpRequest();
    request.responseType = 'blob';
    const fileUrl = url; // 文件路径
    request.open('GET', fileUrl);
    request.onload = function () {
      const url = window.URL.createObjectURL(this.response);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.href = url;
      if (fileName) a.download = fileName;
      a.click();
    };
    request.send();
  }
}

/**
 * 检查给定的用户代理是否是爬虫。
 *
 * @param {string} [userAgent=navigator.userAgent] - 要检查的用户代理字符串。默认为当前浏览器的用户代理。
 * @return {boolean} 如果用户代理是爬虫，则返回true；否则返回false。
 */
export function isCrawler(userAgent = navigator.userAgent) {
  // 定义搜索引擎爬虫的User-Agent标识符
  const crawlerUserAgents = [
    'Googlebot', // 谷歌
    'Bingbot', // bing
    'BingPreview', // bing 预览
    'Baiduspider', // 百度
    'Yandexbot', // Yandex
    'YisouSpider', // 宜搜
    'Sogou web spider', // 搜狗
    '360Spider', // 360
    'YoudaoBot', // 有道
    'Bytespider', // 字节跳动-头条搜索
    // 可以添加其他搜索引擎爬虫的标识符
  ];
  return crawlerUserAgents.some((crawler) =>
    userAgent.toLocaleLowerCase().includes(crawler.toLocaleLowerCase())
  );
}

export function getQueryString(name: string): string | null {
  const queryParams = new URLSearchParams(window.location.search);
  const paramValue = queryParams.get(name);
  if (paramValue != null) {
    return paramValue;
  }
  return null;
}

export function setNativeRouter(type: number, id?: string) {
  // 38 签到卡跳转   39积分详情跳转
  window.sma.setNativeRouter({ type: type, id: id });
}

export function setNavBar(title: string) {
  //
  window.sma.setNavBar({ title: title });
}

/**
 * 比较两个版本号的大小
 * @param {string} version1 - 第一个版本号
 * @param {string} version2 - 第二个版本号
 * @returns {number} - 如果version1 > version2，返回1；如果version1 < version2，返回-1；如果version1 == version2，返回0
 */
export function compareVersions(version1 = '', version2 = '') {
  // 将版本号按点号分隔为数组
  const v1Parts = version1.split('.').map(Number);
  const v2Parts = version2.split('.').map(Number);

  // 确定最长的数组长度
  const maxLength = Math.max(v1Parts.length, v2Parts.length);

  // 比较每一部分的数值
  for (let i = 0; i < maxLength; i++) {
    const v1Part = v1Parts[i] || 0; // 如果v1Parts数组长度不足，用0补齐
    const v2Part = v2Parts[i] || 0; // 如果v2Parts数组长度不足，用0补齐

    if (v1Part > v2Part) {
      return 1;
    } else if (v1Part < v2Part) {
      return -1;
    }
  }

  // 如果所有部分都相等，返回0
  return 0;
}

/**
 * 检查当前环境是否为科学中国App。
 * @returns {boolean} 如果是科学中国App则返回true，否则返回false。
 */
export function isApp() {
  return window.sma?.isApp?.scienceChina ?? false;
}

/**
 * 通过app登录，获取并设置用户登录信息。
 * 1. 重置用户存储中的登录信息。
 * 2. 从app中获取 token。
 * 3. 获取到token时，调用接口获取M站的用户token和登录信息。
 * 4. 未获取到token时，如果force为true，则唤起app登录界面。
 * @param {boolean} [force=true] 是否未登录时强制唤起app登录界面，默认强制唤起。
 */
export async function loginByApp(force = true) {
  try {
    const userStore = useUserStore();
    userStore.resetLoginUser();
    const appToken = window.sma?.getToken()?.token ?? '';
    if (appToken) {
      const res = await reqExchageToken({
        token: appToken,
      });
      userStore.setLoginUser(res.data);
    } else if (force) {
      window.sma?.login?.();
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * 检查用户是否已登录。
 * @returns {boolean} 如果用户已登录（存在token），返回true；否则返回false。
 */
export async function isLogin() {
  if (isApp()) {
    return Boolean(window.sma?.getToken()?.token ?? '');
  } else if (isKeXieApp()) {
    const token = await getKeXieAppToken();
    return Boolean(token);
  } else {
    const userStore = useUserStore();
    return Boolean(userStore.token);
  }
}

/**
 * 登录功能，根据当前环境执行不同的登录逻辑：
 * - 如果是应用环境，调用 `window.sma.login` 方法。
 * - 如果是非应用环境，通过路由跳转到登录页，并附带当前路径作为重定向参数。
 */
export function toLogin() {
  if (isApp()) {
    window.sma?.login?.();
  } else if (isKeXieApp()) {
    loginByKeXieApp();
  } else {
    router.push(
      '/login?redirect=' +
        encodeURIComponent(router.currentRoute.value.fullPath)
    );
  }
}

/**
 * 检测当前运行环境是否为微信浏览器
 * @returns {boolean} 如果是微信浏览器返回true，否则返回false
 */
export function isWeiXin() {
  const ua = window.navigator.userAgent.toLowerCase();
  return ua.indexOf('micromessenger') > -1;
}

/**
 * 检测当前运行环境是否为iOS设备
 * @returns {boolean} 如果是iOS设备返回true，否则返回false
 */
export function isiOS() {
  const ua = window.navigator.userAgent.toLowerCase();
  return ua.indexOf('iphone') > -1 || ua.indexOf('ipad') > -1;
}

/**
 * 检查是否为安卓
 * @returns {boolean}
 */
export function isAndroid() {
  const ua = window.navigator.userAgent.toLowerCase();
  return ua.indexOf('android') > -1 || ua.indexOf('adr') > -1;
}

/**
 * 检测当前运行环境是否为Harmony系统
 * @returns {boolean} 如果是Harmony系统返回true，否则返回false
 */
export function isHarmony() {
  const ua = window.navigator.userAgent.toLowerCase();
  return ua.indexOf('harmony') > -1;
}

/**
 * 格式化文件大小为易读的字符串表示
 * @param {number} size - 文件大小，单位为字节
 * @param {number} [fractionDigits=2] - 保留的小数位数，默认为2位
 * @returns {string} 格式化后的文件大小字符串（如"1.23MB"）
 */
export function fileSizeFormat(size: number, fractionDigits = 2) {
  if (size < 1024) {
    return size + 'B';
  } else if (size >= 1024 && size < Math.pow(1024, 2)) {
    return parseFloat(String(size / 1024)).toFixed(fractionDigits) + 'KB';
  } else if (size >= Math.pow(1024, 2) && size < Math.pow(1024, 3)) {
    return (
      parseFloat(String(size / Math.pow(1024, 2))).toFixed(fractionDigits) +
      'MB'
    );
  } else if (size > Math.pow(1024, 3)) {
    return (
      parseFloat(String(size / Math.pow(1024, 3))).toFixed(fractionDigits) +
      'GB'
    );
  } else {
    return 0 + 'B';
  }
}

/**
 * 关闭窗口
 *
 */
export function closeWindow() {
  window.sma.closeWindow();
}
